import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import UploadFileBtn from "../Helper/UploadFileBtn/UploadFileBtn";
import { downloadDocument, getDocuments, uploadMultipleDocuments } from "../Helper/DocumentsHelper";
import { Box, Button, Typography, Paper, Alert, AlertTitle, Divider, CircularProgress, Tooltip, IconButton, Snackbar } from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DocumentsTypes } from "../Enum/DocumentsType";
import "./AmazonReceiptUploadFiles.scss";
import { useTranslation } from "react-i18next";

const AmazonReceiptUploadFiles = () => {
    const { t } = useTranslation("amazonReceiptUploadFiles");

    const dispatch = useDispatch();
    const defaultAccount = useSelector((state: any) => state.account.default_account);

    const [documents, setDocuments] = useState<any[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const [toastOpen, setToastOpen] = useState(false);
    const [toastMsg, setToastMsg] = useState("");

    const canWork = useMemo(() => Boolean(defaultAccount?.id), [defaultAccount]);

    const getDocumentsAccount = async () => {
        if (!defaultAccount?.id) return;

        setIsLoading(true);
        setError(null);

        try {
            const docs = await getDocuments(dispatch, DocumentsTypes.AMAZON_RECEIPT, defaultAccount.id);
            setDocuments(docs || []);
        } catch (err) {
            setError(t("errors.fetchFailed"));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getDocumentsAccount();
    }, [defaultAccount?.id]);

    const handleFileUpload = async (file: File | File[] | FileList) => {
        if (!defaultAccount?.id) {
            setError(t("errors.noAccountSelected"));
            return;
        }

        setIsUploading(true);
        setError(null);

        try {
            const res = await uploadMultipleDocuments(dispatch, file, DocumentsTypes.AMAZON_RECEIPT, defaultAccount.id);
            if (!res) {
                throw new Error("Upload failed");
            }
            setDocuments(res.documents || []);
            if (res.upload.message === "success") {
                setToastMsg(t("toast.uploaded"));
                setToastOpen(true);
                return;
            }

            if (res.upload.message === "partial_success") {
                setToastMsg(
                    t("toast.partial", {
                        uploaded: res.upload.uploaded,
                        total: res.upload.total,
                    })
                );
                setToastOpen(true);
                return;
            }

            throw new Error("Upload failed");
        } catch (err) {
            setError(t("errors.uploadFailed"));
        } finally {
            setIsUploading(false);
        }
    };

    const filesCountText = t("list.filesCount", { count: documents.length });

    return (
        <Box className="amazonReceipt">
            {/* Header */}
            <Box className="amazonReceipt__header">
                <Box>
                    <Typography variant="h4" component="h2" className="amazonReceipt__title">
                        {t("title")}
                    </Typography>
                    <Typography variant="body2" className="amazonReceipt__subtitle">
                        {t("subtitle")}
                    </Typography>
                </Box>

                <Tooltip title={t("tooltips.info")}>
                    <IconButton className="amazonReceipt__infoBtn" size="small" aria-label={t("aria.info")}>
                        <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Error */}
            {error && (
                <Alert severity="error" className="amazonReceipt__alert" variant="outlined">
                    <AlertTitle>{t("errorBanner.title")}</AlertTitle>
                    {error}
                </Alert>
            )}

            {/* Upload Card */}
            <Paper className="amazonReceipt__uploadCard" elevation={0}>
                <Box className="amazonReceipt__uploadCardTop">
                    <Box className="amazonReceipt__uploadIconWrap" aria-hidden="true">
                        <CloudUploadOutlinedIcon />
                    </Box>

                    <Box className="amazonReceipt__uploadText">
                        <Typography variant="h6" className="amazonReceipt__uploadTitle">
                            {t("uploadCard.title")}
                        </Typography>
                        <Typography variant="body2" className="amazonReceipt__uploadHint">
                            {t("uploadCard.hint")}
                        </Typography>
                    </Box>
                </Box>

                <Divider className="amazonReceipt__divider" />

                <Box className="amazonReceipt__uploadActions">
                    <UploadFileBtn
                        onSubmit={handleFileUpload}
                        multiple={true}
                        title=""
                        buttonText={t("buttons.upload")}
                        exdendStyle={true}
                        classes="amazonReceipt__uploadBtn"
                        autoUpload={true}
                    />

                    {isUploading && (
                        <Box className="amazonReceipt__uploadProgress" aria-label={t("aria.uploading")}>
                            <CircularProgress size={18} />
                            <Typography variant="caption">{t("states.uploading")}</Typography>
                        </Box>
                    )}

                    {!canWork && (
                        <Typography variant="caption" className="amazonReceipt__disabledHint">
                            {t("states.selectAccountToEnable")}
                        </Typography>
                    )}
                </Box>
            </Paper>

            {/* List */}
            <Box className="amazonReceipt__list">
                <Box className="amazonReceipt__listHeader">
                    <Typography variant="h6">{t("list.title")}</Typography>
                    <Typography variant="body2" className="amazonReceipt__listSub">
                        {filesCountText}
                    </Typography>
                </Box>

                {isLoading ? (
                    <Box className="amazonReceipt__loading">
                        <CircularProgress size={22} />
                        <Typography variant="body2">{t("states.loadingDocuments")}</Typography>
                    </Box>
                ) : documents.length > 0 ? (
                    <Box className="amazonReceipt__grid">
                        {documents.map((doc) => (
                            <Paper key={doc.id} className="amazonReceipt__docCard" elevation={0}>
                                <Box className="amazonReceipt__docLeft">
                                    <Box className="amazonReceipt__docIcon" aria-hidden="true">
                                        <DescriptionOutlinedIcon />
                                    </Box>

                                    <Box className="amazonReceipt__docMeta">
                                        <Typography variant="body1" className="amazonReceipt__docName" title={doc.name}>
                                            {doc.name}
                                        </Typography>
                                        <Typography variant="caption" className="amazonReceipt__docDate">
                                            {doc.upload_date}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Button
                                    variant="outlined"
                                    size="small"
                                    className="amazonReceipt__downloadBtn"
                                    startIcon={<DownloadOutlinedIcon />}
                                    onClick={() => downloadDocument(dispatch, defaultAccount.id, doc.id, doc.name)}
                                    disabled={!canWork}
                                >
                                    {t("buttons.download")}
                                </Button>
                            </Paper>
                        ))}
                    </Box>
                ) : (
                    <Paper className="amazonReceipt__empty" elevation={0}>
                        <Typography variant="body1" className="amazonReceipt__emptyTitle">
                            {t("empty.title")}
                        </Typography>
                        <Typography variant="body2" className="amazonReceipt__emptySub">
                            {t("empty.subtitle")}
                        </Typography>
                    </Paper>
                )}
            </Box>

            <Snackbar
                open={toastOpen}
                autoHideDuration={2200}
                onClose={() => setToastOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert onClose={() => setToastOpen(false)} severity="success" variant="filled">
                    {toastMsg}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AmazonReceiptUploadFiles;
